import { type IdToken, type User, useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import Lottie from "lottie-web";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactGA4 from "react-ga4";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { saveTripAdvisorResults } from "../actions/hotels";
import { resetGuestProfile, saveGuestProfile } from "../actions/init";
import { accSearchFormUpdate } from "../actions/search/accommodation_search";
import {
  default_guest_nationality,
  getPageSizeFromSession,
  loadAccsFromSession,
  saveAccSearchSession,
  searchSessionIdle,
  searchSessionLoading,
  setPageSizeToSession,
} from "../actions/single_search/accommodation_search";
import { fetchTripAdvisorData, retrieveSearchSessionMetadata } from "../api";
import { type AppUserDef, getAppUser } from "../api/entity/appuser";
import { userIsB2B } from "../api/entity/appuser_types";
import { AuthEnvContext, brand, custom_auth, ext_crm, withTripadvisor } from "../context";
import type { Guest } from "../definitions";
import { hotelsIsSearchingSelector } from "../umbilical_hocs/hotels_selector";

const withAuth = import.meta.env.VITE_APP_PRIVATE_WEBSITE === "true";
const external_website_url = import.meta.env.VITE_APP_EXT_WEBSITE_URL;
const g4akey = import.meta.env.VITE_APP_GA4_KEY;

const build_type = import.meta.env.VITE_APP_BUILD || "staging";

export function usePropertyLangTag() {
  const { i18n } = useTranslation();
  const lang = i18n?.language ?? "en-US";

  const langTag = lang.toLowerCase().includes("en-") ? "" : `_${lang.split("-")[0]}`.toLowerCase();
  return langTag;
}

export function useMetaStuff() {
  const titleMapping = {
    easy: "easyTravel.tech",
    default: "B2CTrips",
    mse: "My Sneaky Escapes",
    isg: "ISG Global Travel",
    cst: "CST Global Travel",
    mycarte: "My Carte Travel",
    soulatravel: "Soula Travel",
    corporatetravelclub: "The Corporate Travel Club",
    unionshopper: "Union Shopper",
    thehotelco: "thehotelco",
    tmdh: "TMDH",
    clubseacret: "ClubSeacret",
    infinite: "Infinite Travel",
    festival: "Festival Travel",
    atlantic: "Atlantic Travel",
    knoxtravel: "Knox Travel",
    traveldealfinders: "Travel Deal Finders",
    rocs: "ROCS Travel",
    fightzone: "Fight Zone Travel",
    travellingtime: "Travelling Time",
    htg: "Haritou",
    flymenow: "FLY ME NOW",
    truecations: "Truecations",
    oasis: "Oasis Travel Network",
    greecetrips: "Explore the Greek Islands",
    innstanttravel: "INNstant TRAVEL",
    uab: "UAB Baltic Business Tour",
    maldives: "Easy Maldives",
    miki: "Miki Plus",
    eko: "Eko Corporate Travel Portal",
    helleniq: "HelleniqEnergy Corporate Travel Portal",
    interamerican: "Interamerican Corporate Travel Portal",
    mytravellifestyle: "My Travel Lifestyle",
    hgv: "Hgv",
    kemet: "Kemet Travel",
    travily: "Travily",
    mts: "MTS Test Demo",
    clubwyndham: "Voyager",
    accorvacationclub: "Accor Vacation Club",
  };

  const faviconMapping = {
    easy: "https://apollob2c-demo.s3.ap-southeast-1.amazonaws.com/easy/assets/favicon_easy.ico",
    thehotelco: "https://thehotelco-assets.s3.eu-west-2.amazonaws.com/favicons/favicon-32x32.png",
    infinite: "https://apollo-infinite.s3.amazonaws.com/assets/favicon.png",
    traveldealfinders:
      "https://www.traveldealfinders.com.au/wp-content/uploads/2020/10/favicon.png",
    tmdh: "https://apollo-tmdh-staging.s3.eu-west-2.amazonaws.com/assets/favicon-32x32.png",
    rocs: "https://static.wixstatic.com/media/83fcaa_225532a6afe247efae08228d7106eb79~mv2.png/v1/fill/w_32%2Ch_32%2Clg_1%2Cusm_0.66_1.00_0.01/83fcaa_225532a6afe247efae08228d7106eb79~mv2.png",
    travellingtime:
      "https://travellingtime.co.uk/wp-content/uploads/2021/03/cropped-Logo-in-a-box-Mark-2-1-32x32.jpg",
    oasis: "https://apollo-oasis.s3.amazonaws.com/assets/favicon_oasis.png",
    greecetrips: "https://greecetrips.s3.amazonaws.com/assets/favicon_greece_trips.png",
    flymenow: "https://apollo-flymenow.s3.ap-southeast-2.amazonaws.com/assets/favicon.png",
    innstanttravel: "https://apollo-innstanttravel.s3.eu-west-1.amazonaws.com/assets/favicon.ico",
    uab: "https://apollo-uab.s3.eu-north-1.amazonaws.com/assets/favicon.png",
    maldives:
      "https://apollo-easymaldives.s3.eu-west-3.amazonaws.com/assets/round-only-palm-red-lines.png",
    mytravellifestyle:
      "https://mytravellifestyle.s3.ap-southeast-1.amazonaws.com/assets/favicon_mytravellifestyle.png",
    hgv: "https://apollo-hgv.s3.ap-southeast-1.amazonaws.com/assets/favicon-hgv.png",
    kemet: "https://apollo-kemet.s3.eu-central-1.amazonaws.com/assets/favicon_kemet.png",
    festival:
      "https://apollo-festivaltravel.s3.ap-southeast-2.amazonaws.com/assets/favicon_festival.jpg",
    travily: "https://apollo-travily.s3.amazonaws.com/assets/favicon+travily.png",
    miki: "https://apollo-miki.s3.ap-southeast-1.amazonaws.com/assets/miki_favicon.png",
    mts: "https://apollo-mtsbeta.s3.amazonaws.com/assets/favicon2.ico",
    clubwyndham: "https://apollo-clubwyndham.s3.ap-southeast-1.amazonaws.com/assets/favicon.png",
    accorvacationclub:
      "https://apollo-accorvacationclub.s3.ap-southeast-1.amazonaws.com/assets/favicon.png",
  };

  useEffect(() => {
    document.title = _.get(titleMapping, brand);
    const fav = _.get(faviconMapping, brand, "");
    if (fav) {
      const el = document.getElementById("favicon");
      if (el) {
        el.href = fav;
      }
    }

    if (!g4akey) return;

    ReactGA4.initialize(g4akey);
    ReactGA4.send({ hitType: "pageview", page: window.location.pathname });
  }, []);
}

export function useQueryParams() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Get source_entity, market_type and website_api_key from the website metadata.
 * If the market_type is B2B, then the source_entity is taken from the user
 * profile.
 * @param {object} props
 * @param {object} props.user
 * @returns {object: { market_type, source_entity and website_api_key }} market_type, source_entity and website_api_key
 */
export function useSourceEntity({ user }) {
  let { market_type, source_entity, website_api_key } = useSelector((state: any) => {
    return {
      market_type: state.WebsiteMetadata.metadata.market_type,
      source_entity: state.WebsiteMetadata.metadata.source_entity,
      website_api_key: state.WebsiteMetadata.metadata.api_key,
    };
  });

  const { getIdTokenClaims } = useAuth0();
  const isB2bUser = userIsB2B(user);

  const { data: appUser } = useQuery({
    queryKey: ["appUser", user?.sub],
    queryFn: async () => {
      if (!user?.sub) return null;
      const token: IdToken | undefined = await getIdTokenClaims();
      if (!token || !("__raw" in token)) return null;
      return getAppUser(user.sub, (token as { __raw: string }).__raw);
    },
    enabled: market_type === "B2B" && !!user && isB2bUser && !!getIdTokenClaims,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const appUserData = appUser?.data;

  if (market_type === "B2B") {
    // First try to get source_entity from API response
    if (appUserData?.entity_id && appUserData?.entity_type) {
      source_entity = `${appUserData.entity_id}___${appUserData.entity_type}`;
    } else {
      // Fallback to Auth0 user mark data
      const mark = user?.["https://sisitrip.com"]?.mark ?? {};
      if (mark.company_id && mark.company_type) {
        source_entity = `${mark.company_id}___${mark.company_type}`;
      }
    }
  }
  return { source_entity, market_type, website_api_key, appUserData };
}

export function useApoAuth(): {
  isB2B: boolean;
  source_entity: string;
  market_type: "B2B" | "B2C";
  website_api_key: string;
  user: User | undefined;
  isAuthenticated: boolean;
  getIdTokenClaims: () => Promise<IdToken | undefined>;
  logout?: () => void;
  appUserData?: AppUserDef;
} {
  const {
    user,
    isAuthenticated: isAuth0Authenticated,
    logout: auth0Logout,
    getIdTokenClaims,
  } = useAuth0();
  const { market_type, source_entity, website_api_key, appUserData } = useSourceEntity({
    user,
  });

  const guestProfile = useSelector((state: { GuestProfile: any }) => state.GuestProfile);

  const dispatch = useDispatch();
  const saveExternalGuest = useCallback(
    (guestProfile: any) => dispatch(saveGuestProfile(guestProfile)),
    [dispatch],
  );
  const resetGuest = useCallback(() => dispatch(resetGuestProfile()), [dispatch]);

  const commonReturn = {
    isB2B: market_type === "B2B",
    source_entity,
    market_type,
    website_api_key,
    user,
    getIdTokenClaims,
    appUserData,
  };

  if (!withAuth) {
    return { isAuthenticated: false, ...commonReturn };
  }

  if (custom_auth) {
    function logout() {
      window.localStorage.removeItem("extProfile");
      resetGuest();
      window.location.href = external_website_url;
    }

    if (_.isEmpty(guestProfile?.extProfile)) {
      const rawSession = window.localStorage.getItem("extProfile");
      if (!rawSession) {
        return { isAuthenticated: false, logout, ...commonReturn };
      }

      let extProfile = null;
      try {
        const tmp = window.localStorage.getItem("extProfile");
        if (tmp) extProfile = JSON.parse(tmp);
      } catch {
        return { isAuthenticated: false, logout, ...commonReturn };
      }
      saveExternalGuest({ extProfile });
    }
    return { isAuthenticated: true, logout, ...commonReturn };
  }

  return {
    isAuthenticated: isAuth0Authenticated,
    logout: auth0Logout,
    ...commonReturn,
  };
}

export function useListPage() {
  const [data, setData] = useState({});

  const { list_pages } = useSelector((state) => ({
    list_pages: _.get(state.WebsiteMetadata, "metadata.list_pages", []),
  }));

  const location = useLocation();
  const { path } = useRouteMatch();
  const regex = new RegExp("/", "g");
  const check = path.replace(regex, "");

  useEffect(() => {
    if (list_pages.length > 0) {
      const productSplashDataTemp = list_pages.filter((page) => page.path === check);
      setData(productSplashDataTemp ? productSplashDataTemp[0] : {});
    }
  }, [list_pages, location]);

  return data;
}

export function useBaseReservationPayload({ group_leader }: { group_leader: Guest }): {
  first_name: string;
  last_name: string;
  email: string;
  customer_uid: string;
  nationality: string;
  customer_stripe_id: string;
} {
  const guestProfile = useSelector((state: { GuestProfile: any }) => state.GuestProfile);

  const { withAuth } = useContext(AuthEnvContext);
  const { user } = useAuth0();

  var customer_uid: string | null = null;

  var first_name = (group_leader?.first_name ?? "") as string;
  var last_name = (group_leader?.last_name ?? "") as string;
  var email = (group_leader?.email ?? "") as string;

  var customer_stripe_id = (guestProfile?.stripe_id ?? "") as string;

  if (withAuth) {
    customer_uid = guestProfile?.ext_user_id;

    if (!customer_uid) {
      if (!!user && !!user.sub) customer_uid = user.sub;
    }

    first_name = (guestProfile?.first_name as string) ?? first_name;
    last_name = (guestProfile?.last_name as string) ?? last_name;
    email = (guestProfile?.email as string) ?? email;
  }

  if (custom_auth) {
    customer_uid = guestProfile?.extProfile?.ext_user_id;
    first_name = guestProfile?.extProfile?.first_name ?? "";
    last_name = guestProfile?.extProfile?.last_name ?? "";
    email = guestProfile?.extProfile?.email ?? "";
  } else if (ext_crm) {
    customer_uid = guestProfile?.extProfile?.stripe_customer_id;
    first_name = guestProfile?.extProfile?.first_name ?? "";
    last_name = guestProfile?.extProfile?.last_name ?? "";
    email = guestProfile?.extProfile?.email ?? "";
    customer_stripe_id = guestProfile?.extProfile?.stripe_customer_id ?? "";
  }

  const nationality = !!group_leader?.nationality
    ? group_leader.nationality
    : default_guest_nationality;

  if (!withAuth) {
    customer_uid = group_leader?.uid ?? "";
  } else if (!email) {
    email = group_leader?.email ?? "";
  }

  return {
    first_name,
    last_name,
    email,
    customer_uid: customer_uid || "",
    nationality,
    customer_stripe_id,
  };
}

/**
 * TODO: To add description here.
 * @param {string} page_session_id: The session metadata stored
 * in the Canaveral service
 */
export function useHotelSearchSession({ page_session_id }: { page_session_id: string }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const onSaveAccSession = useCallback(
    ({ session_data, history }) => dispatch(saveAccSearchSession({ session_data, history })),
    [dispatch],
  );

  useEffect(() => {
    retrieveSearchSessionMetadata({ page_session_id }).then((data) => {
      onSaveAccSession({ session_data: data.session_data.data, history });
    });
  }, []);
}

export function useHotelsFromSession({ hotels, isSearching, page_session_id }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const onLoading = useCallback(() => dispatch(searchSessionLoading()), [dispatch]);
  const onIdle = useCallback(() => dispatch(searchSessionIdle()), [dispatch]);
  const onSaveAccSession = useCallback(
    ({ session_data, history }) => dispatch(loadAccsFromSession({ session_data, history })),
    [dispatch],
  );

  const onUpdateSearchForm = useCallback(
    (form) => dispatch(accSearchFormUpdate({ form })),
    [dispatch],
  );

  useEffect(() => {
    if (hotels.length === 0 && !isSearching) {
      onLoading();
      retrieveSearchSessionMetadata({ page_session_id }).then((data) => {
        if (!data) {
          history.push("/404/");
          return;
        }
        onSaveAccSession({ session_data: data.session_data.data, history });
        onUpdateSearchForm({
          ...data.session_data.data.searchData,
        });
        onIdle();
      });
    }
  }, [isSearching]);
}

export function useHotelsListLimit({ defaultPageSize }) {
  const hotel_filters = useSelector((state) => state.HotelSearch);

  const [listLimit, setListLimit] = useState(getPageSizeFromSession(defaultPageSize));

  // When filters change reset list limit.
  useEffect(() => {
    setListLimit(defaultPageSize);
    setPageSizeToSession(defaultPageSize);
  }, [hotel_filters]);

  function increaseListSize() {
    setPageSizeToSession(listLimit + defaultPageSize);
    setListLimit((p) => p + defaultPageSize);
    ReactGA4.event({
      category: "action",
      action: "Show_more_hotels",
      label: "Show_more_hotels",
      value: listLimit + defaultPageSize,
    });
  }

  return { listLimit, setListLimit, increaseListSize };
}

export function useTripAdvisor({ visibleHotels }) {
  const [tripadvisorLoading, setTripadvisorLoading] = useState(false);

  const { isSearching, tripAdvisorHotels, hotel_filters } = useSelector((state) => {
    return {
      isSearching: hotelsIsSearchingSelector(state),
      tripAdvisorHotels: state.tripAdvisorReducer,
      hotel_filters: state.HotelSearch,
    };
  });

  const dispatch = useDispatch();
  const saveTripAdvRes = useCallback(
    ({ results }) => dispatch(saveTripAdvisorResults({ results })),
    [dispatch],
  );

  useEffect(() => {
    if (isSearching) {
      return;
    }

    if (!withTripadvisor) {
      return;
    }

    if (!visibleHotels.length) {
      return;
    }

    const hotel_ids = visibleHotels
      .map((htl) => htl.metadata.id)
      .filter((id) => !Object.keys(tripAdvisorHotels).includes(id));

    const promises = _.chunk(hotel_ids, 10).map((ch) => fetchTripAdvisorData({ hotel_ids: ch }));

    setTripadvisorLoading(true);
    Promise.all(promises)
      .then((chunks_results) => {
        const hotel_data = {};
        chunks_results.forEach((data) => {
          data.forEach((htl) => {
            hotel_data[htl.hotel_id] = htl;
          });
        });
        saveTripAdvRes({ results: hotel_data });
        setTripadvisorLoading(false);
      })
      .catch((data) => {
        return;
      });
  }, [visibleHotels.length, hotel_filters, isSearching]);
  return { tripadvisorLoading, tripAdvisorHotels };
}

export function useHotelTypesSearch() {
  const { hotel_acc_autocomplete, hotel_dest_autocomplete } = useSelector((state) => {
    const hotel_acc_autocomplete = state?.WebsiteMetadata?.metadata?.hotel_acc_autocomplete;
    const hotel_dest_autocomplete = state?.WebsiteMetadata?.metadata?.hotel_dest_autocomplete;
    return {
      hotel_acc_autocomplete,
      hotel_dest_autocomplete,
    };
  });
  const types = [];
  if (hotel_acc_autocomplete) {
    types.push("ACC");
  }
  if (hotel_dest_autocomplete) {
    types.push("DEST");
  }
  return { types };
}

/**
 * @param {*} pageSize (optional) the number of items to show per page
 * @returns the current page limit and a function to increase the page limit
 */
export function usePageLimit(pageSize) {
  const defaultPageSize = pageSize || 10;

  const [pageLimit, setPageLimit] = useState(defaultPageSize);

  function increasePageSize() {
    setPageLimit((p) => p + defaultPageSize);
  }

  return { pageLimit, increasePageSize };
}

export function useGetPagesSplashData() {
  const langTag = usePropertyLangTag();

  const [data, setData] = useState({});

  const location = useLocation();
  const { pathname } = location;

  const regex = new RegExp("/", "g");
  const listPageName = pathname.replace(regex, "");

  const {
    title: titleLP,
    subtitle: subtitleLP,
    extra_titles: extra_titlesLP,
    extra_subtitles: extra_subtitlesLP,
    images: imagesLP,
    description: descriptionLP,
    call_to_actions,
  } = useSelector((state) => {
    const landingPage = state.WebsiteMetadata?.metadata?.landing_page ?? {};

    const title = landingPage?.[`title${langTag}`] ?? "";
    const subtitle = landingPage?.[`subtitle${langTag}`] ?? "";
    const description = landingPage?.[`description${langTag}`] ?? "";

    const images = landingPage?.images ?? [];
    const extra_titles = landingPage?.extra_titles ?? [];
    const extra_subtitles = landingPage?.extra_subtitles ?? [];
    const call_to_actions = landingPage?.call_to_actions ?? [];
    return {
      title,
      subtitle,
      description,
      images,
      extra_titles,
      extra_subtitles,
      call_to_actions,
    };
  });

  const { list_pages } = useSelector(
    (state: { WebsiteMetadata: { metadata: { list_pages: { path: string }[] } } }) =>
      state.WebsiteMetadata?.metadata ?? {},
  );
  const otherPageData = list_pages.find((page) => page?.path === listPageName);

  useEffect(() => {
    if (pathname === "/") {
      setData({
        titles: [titleLP, ...(extra_titlesLP || [])],
        subtitles: [subtitleLP, ...(extra_subtitlesLP || [])],
        images: imagesLP,
        descriptionLP,
        call_to_actions,
      });
    } else if (otherPageData) {
      setData({
        titles: [otherPageData?.[`title${langTag}`], ...(otherPageData.extra_titles || [])],
        subtitles: [
          otherPageData?.[`subtitle${langTag}`],
          ...(otherPageData.extra_subtitles || []),
        ],
        images: otherPageData.images,
        description: otherPageData?.[`description${langTag}`],
        call_to_actions: otherPageData?.call_to_actions ?? [],
      });
    }
  }, [titleLP, otherPageData]);

  return data;
}

export function useMultipleRoomsByBrand() {
  const brandList = [
    "easy",
    "rocs",
    "knoxtravel",
    "festival",
    "atlantic",
    "innstanttravel",
    "tmdh",
    "flymenow",
    "oasis",
    "traveldealfinders",
    "infinite",
  ];
  return (brand === "easy" && build_type === "staging") || brandList.includes(brand);
}

export function useLoadingAnimation({ loading, animationName, color = "#ff6600" }) {
  const animationRef = useRef(null);
  const [animation, setAnimation] = useState(null);
  const destroy = () => {
    if (animation) {
      animation.destroy();
    }
  };
  useEffect(() => {
    if (!loading || !animationRef.current) {
      destroy();
      return destroy;
    }
    const a = Lottie.loadAnimation({
      container: animationRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: `https://apollo-statics.s3.eu-central-1.amazonaws.com/svgs/${animationName}.json`,
    });
    setAnimation(a);

    if (color !== "") {
      const intervalId = setInterval(() => {
        if (!animationRef?.current) return;

        const elements = (animationRef?.current as HTMLElement)?.querySelectorAll("*");
        // Change the fill color of orange
        elements.forEach((element) => {
          if (element?.getAttribute("fill")) {
            if (
              element?.getAttribute("fill") === "rgb(255,102,0)" ||
              element?.getAttribute("fill") === "#ff6600"
            ) {
              element?.setAttribute("fill", color);
            }
          }
          if (element?.getAttribute("stroke")) {
            if (
              element?.getAttribute("stroke") === "rgb(255,102,0)" ||
              element?.getAttribute("stroke") === "#ff6600"
            ) {
              element?.setAttribute("stroke", color);
            }
          }
        });
      }, 100);

      return () => {
        clearInterval(intervalId);
        destroy();
      };
    } else {
      return destroy;
    }
  }, [loading]);
  return { animationRef };
}
